/* eslint-disable no-console */
import * as Sentry from '@sentry/browser';
import { useEffect, useRef } from 'react';
import { useErrorBoundary } from './hooks/useErrorBoundary';
import { ChildrenOnly } from './types/ChildrenOnly';
import { returnVoid } from './util/returnVoid';

export const SentryErrorBoundary = ({ children }: ChildrenOnly) => {
  const [wrap, caught, clear] = useErrorBoundary();

  /** If we are caught in an error loop, we don't want to saturate our Sentry allocation */
  const backoffMs = useRef(100);

  useEffect(() => {
    if (caught) {
      logErrorToSentry(caught.error, caught.info);

      setTimeout(clear, backoffMs.current);
      backoffMs.current *= 2;
    }
  }, [caught, clear]);

  return wrap(children);
};

export const logErrorToSentry = (error: unknown, extra?: unknown) => {
  Sentry.withScope(scope => {
    if (extra) {
      for (const [key, value] of Object.entries(extra)) scope.setExtra(key, value);
    }

    Sentry.captureException(error);
  });

  console.group(String(error));

  if (extra) {
    /** IE doesn't support console.table, and we don't want to cause even more issue noise there */
    if (typeof console.table === 'function') {
      console.table(extra);
    } else {
      console.log(extra);
    }
  }

  console.groupEnd();
};

/**
 * Runs an async task, ignoring its result, and logs any errors to Sentry.
 * @param label - A label which will appear in sentry
 * @param asyncTask - The task to run
 */

export const logRejectionsToSentry = (label: string, asyncTask: () => PromiseLike<unknown>) => {
  const promise = asyncTask();

  promise.then(returnVoid, (cause: unknown) => {
    logErrorToSentry(new Error(`Promise rejected: ${label}: ${String(cause)}`), { cause });
  });
};

export const SENTRY_DENY_URLS = [
  // Ignore Google flakiness and issues from AdBlockers
  /\/(gpt|gtm|ga|analytics)\.js/i,
  // Chrome extensions
  /extensions\//i,
  /^chrome:\/\//i,
  // Safari extensions
  /safari-web-extension:\/\//i,
];

// https://docs.sentry.io/platforms/javascript/guides/nextjs/configuration/filtering/#decluttering-sentry
export const SENTRY_IGNORE_ERRORS = [
  // Random plugins/extensions
  'top.GLOBALS',
  // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
  'originalCreateNotification',
  'canvas.contentDocument',
  'MyApp_RemoveAllHighlights',
  'http://tt.epicplay.com',
  "Can't find variable: ZiteReader",
  'jigsaw is not defined',
  'ComboSearch is not defined',
  'http://loading.retry.widdit.com/',
  'atomicFindClose',
  // Facebook borked
  'fb_xd_fragment',
  // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to
  // reduce this. (thanks @acdha)
  // See http://stackoverflow.com/questions/4113268
  'bmi_SafeAddOnload',
  'EBCallBackMessageReceived',
  // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
  'conduitPage',

  /* https://stackoverflow.com/questions/55738408/javascript-typeerror-cancelled-error-when-calling-fetch-on-ios
   * For security reasons data from fetch fails are available inside of browser dev tools but not javascript
   * Follow up isn't possible on these generic errors
   */
  'TypeError: Failed to fetch',
  'TypeError: NetworkError when attempting to fetch resource.',
  'TypeError: cancelled',
];
